import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@viome/viome-base/src/api/config';
import { Button } from '@design-system/Button';
import { FullScreenPageContainer } from '@/components/utils/FullScreenPageContainer';
import * as P from '@/constants/paths';
import { linkoutConfigs } from '@/config/routing/LinkoutConfig';
import { useUserStore } from '@/hooks/useUserStore';
import useRegisterStore from '@/hooks/useRegistrationStore';
import { useContextStore } from '@/hooks/context/useContextStore';
import { Player } from '@lottiefiles/react-lottie-player';
import SuccessLottie from '@/assets/success-black.json';

export const RegistrationSuccess = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: userData } = useUserStore();
  const proKitOrg = useRegisterStore((state) => state.proKitOrg);
  const setProKitOrg = useRegisterStore((state) => state.setProKitOrg);
  const setPatientOrg = useContextStore((state) => state.setPatientOrg);

  // A logged-in patient who just registered a PRO kit is re-resolved through
  // the experience picker so the just-registered org is picked up: it shows the
  // switch screen (both experiences) or auto-selects (patient-only), then
  // forwards to the questionnaire (via `redirect_path`).
  const isPatientCompletion = !!userData && !!proKitOrg;

  const actionLabel = userData
    ? proKitOrg
      ? 'Complete questionnaire'
      : 'Track your sample'
    : 'Login to my account';

  const handleAction = () => {
    if (isPatientCompletion && proKitOrg) {
      // Promote kit-ID-time org into context store for ExperiencePicker routing.
      setPatientOrg(proKitOrg);
      // Drop the persisted register-store copy — otherwise a later session
      // (sessionStorage cleared, localStorage still has proKitOrg) auto-selects
      // this org and skips the real multi-experience choice.
      setProKitOrg(null);
      // REMOVE (not just invalidate) the cached payloads so the picker remounts
      // with no data -> it shows loading and resolves on FRESH content. With a
      // background invalidate, React Query serves the stale (pre-registration,
      // empty-org) cache first, so the picker would auto-select consumer and
      // open the old consumer questionnaire before the new org arrives.
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.PATIENT_CONTEXT_CONTENT],
      });
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.INIT_DATA_V2] });
      navigate(
        `${P.EXPERIENCE_PICKER_PATH}?redirect_path=${P.QUESTIONNAIRES_PATH}`,
      );
      return;
    }
    navigate(userData ? P.HOME_PATH : P.LOGIN_PATH);
  };

  return (
    <FullScreenPageContainer hideBackButton centerContent data-testid="registrationSuccess-container">
      <Player
        autoplay
        src={SuccessLottie}
        keepLastFrame
        className="w-52 h-52 object-cover"
        data-testid="registrationSuccess-animation"
      />
      <h2 className="text-3xl font-sans md:text-center mb-4 font-medium text-center" data-testid="registrationSuccess-title">
        Success !
      </h2>
      <div className="flex justify-center" data-testid="registrationSuccess-description">
        <p className="font-sans md:text-center">
          Your Kit has been successfully registered
        </p>
      </div>
      <Button
        type="button"
        onClick={handleAction}
        className="w-full py-3 text-white font-semibold bg-black rounded-lg mt-8"
        data-testid="registrationSuccess-actionButton"
      >
        {actionLabel}
      </Button>
      <div className="flex justify-center mt-4" data-testid="registrationSuccess-familyInfo">
        <p className="font-sans text-center">
          If you are registering a kit for a family member, tap{' '}
          <a
            style={{
              textDecoration: 'underline',
              textDecorationColor: 'black',
              textDecorationThickness: '1.4px',
              textUnderlineOffset: '2px',
            }}
            href={linkoutConfigs.multipleKit}
            onMouseDown={(e) => e.preventDefault()}
            data-testid="registrationSuccess-multipleKitLink"
          >
            here
          </a>{' '}
          for more information.
        </p>
      </div>
    </FullScreenPageContainer>
  );
};
